/* @moldura padrao */
/**
 * page.tsx (ordens-compra) — As ordens de compra do site (ficha #42).
 *
 * A OC do site é o MÍNIMO (dele, 27/09): a compra que se rastreia até o pedido
 * de venda. Cada linha diz de que pedidos ela é, e se já foi ao Tiny.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/auth-context';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { fmt } from '../shared';
import type { OrdemCompra } from './shared';
import { ROTULO_ENVIO } from './shared';

export function OrdensCompraListaPage() {
  const navigate = useNavigate();
  const { pode } = useAuth();
  const [lista, setLista] = useState<OrdemCompra[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<OrdemCompra[]>('/custos-pedido/ordens-compra')
      .then(setLista)
      .catch((err) => setErro(err instanceof ApiError ? err.message : 'Não foi possível carregar.'))
      .finally(() => setCarregando(false));
  }, []);

  return (
    <CustomMolduraPagina
      titulo="Ordens de compra"
      descricao="Compras que entram no custo dos pedidos de venda"
      caminho={[{ rotulo: 'Custos de Pedido', para: '/custos-pedido/lista/bz' }]}
      acoes={
        <div className="d-flex gap-2">
          <button className="btn btn-outline-theme" onClick={() => navigate('/custos-pedido/ordens-compra/fornecedores')}>
            <i className="bi bi-people me-1"></i> Fornecedores
          </button>
          {pode('custos-pedido.pedido.criar') && (
            <button className="btn btn-theme" onClick={() => navigate('/custos-pedido/ordens-compra/nova')}>
              <i className="bi bi-plus-lg me-1"></i> Nova ordem de compra
            </button>
          )}
        </div>
      }
    >
      <div className="card adminuiux-card">
        <div className="card-body">
          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle table-hover mb-0 text-nowrap" style={{ fontSize: '0.85rem' }}>
                <thead>
                  <tr className="text-secondary text-uppercase" style={{ fontSize: '0.75rem' }}>
                    <th>Nº no Tiny</th>
                    <th>Data</th>
                    <th>Loja</th>
                    <th>Fornecedor</th>
                    <th>Pedidos</th>
                    <th className="text-end">Total</th>
                    <th>Tiny</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((o) => {
                    const r = ROTULO_ENVIO[o.envio_tiny] ?? ROTULO_ENVIO.nao_enviada;
                    return (
                      <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/custos-pedido/ordens-compra/${o.id}`)}>
                        <td className="fw-semibold">{o.numero_tiny ? `#${o.numero_tiny}` : <span className="text-secondary">—</span>}</td>
                        <td className="text-secondary">{o.data ? formatarData(o.data) : '—'}</td>
                        <td>
                          <span className="badge text-bg-light border fw-normal">{o.loja_codigo}</span>
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 180 }} title={o.fornecedor ?? ''}>
                          {o.fornecedor ?? <span className="text-secondary">—</span>}
                        </td>
                        <td>
                          {o.vinculos.length === 0 ? (
                            <span className="text-secondary">—</span>
                          ) : (
                            o.vinculos.map((v) => (
                              <span
                                key={v.id}
                                className="badge bg-theme-1-subtle text-theme-1 fw-normal me-1"
                                title={`${v.cliente ?? ''} · ${fmt(v.valor)}`}
                              >
                                {v.pedido_numero ? `Ped. ${v.pedido_numero}` : `OS ${v.ordens_servico_id}`}
                                {o.vinculos.length > 1 && v.pct != null ? ` · ${v.pct.toFixed(0)}%` : ''}
                              </span>
                            ))
                          )}
                        </td>
                        <td className="text-end">{fmt(o.valor_total)}</td>
                        <td>
                          <span className={`badge ${r.classe}`}>
                            <i className={`bi ${r.icone} me-1`}></i>
                            {r.texto}
                          </span>
                        </td>
                        <td className="text-end text-secondary">
                          <i className="bi bi-chevron-right"></i>
                        </td>
                      </tr>
                    );
                  })}
                  {lista.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-5 text-center text-secondary">
                        Nenhuma ordem de compra lançada pelo site ainda.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
